import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

/**
 * Hosts whose failures carry no product signal: analytics/marketing beacons that
 * privacy browsers block outright. Every entry here was observed in patient-web RUM
 * `status_code:0` resource telemetry — do not add a host speculatively, and never add
 * a first-party `*.repeatmd.app` tenant host (those failures are the signal).
 */
const THIRD_PARTY_NOISE_HOSTS = [
  "api.segment.io",
  "cdn.segment.com",
  "cdp.customer.io",
  "gist.build",
  "appsflyersdk.com",
];

export const isThirdPartyNoiseUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return THIRD_PARTY_NOISE_HOSTS.some((host) => url.includes(host));
};

const isSegmentPerformanceMetricsNoise = (message: string | undefined): boolean =>
  Boolean(message?.toLowerCase().includes("error sending segment performance metrics"));

type NetworkTransportEventFields = {
  origin: string;
  isAborted: boolean;
};

/**
 * An aborted request is the browser tearing down an in-flight fetch because the user
 * navigated or backgrounded the tab. It is not a failure. Attribution is structural
 * (`context.isAborted`), never a match on the error text.
 */
export const isIgnorableNetworkTransportEvent = ({
  origin,
  isAborted,
}: NetworkTransportEventFields): boolean => origin === "network" && isAborted;

export const initDatadog = () => {
  if (!import.meta.env.REACT_APP_DD_ENV) {
    console.warn("Datadog RUM not initialized: REACT_APP_DD_ENV is not set");
    return;
  }

  datadogRum.init({
    applicationId: "dfe22215-963f-4e3e-95a0-dca35c91283c",
    clientToken: "pubd1728bd8bc02b6ea16033ce9aee7942e",
    service: "patient-web",
    site: "datadoghq.com",
    env: import.meta.env.REACT_APP_DD_ENV,
    version: import.meta.env.REACT_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 40,
    allowedTracingUrls: [location.origin],
    sessionPersistence: "local-storage",
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "allow",
    beforeSend: (event) => {
      // Only error events are filtered, and only when the event itself names a
      // third-party host. Resource events are deliberately left intact: RUM strips
      // the URL from most transport error events, so resource telemetry is the only
      // surviving attribution for a failed fetch. Suppressing it here would make a
      // session firing hundreds of blocked beacons indistinguishable from a healthy
      // one, and would hide first-party tenant outages entirely.
      if (event.type === "error") {
        if (isThirdPartyNoiseUrl(event.error.resource?.url)) {
          return false;
        }
        if (isSegmentPerformanceMetricsNoise(event.error.message)) {
          return false;
        }
      }
      return true;
    },
  });

  datadogLogs.init({
    clientToken: "pubd1728bd8bc02b6ea16033ce9aee7942e",
    service: "patient-web",
    site: "datadoghq.com",
    env: import.meta.env.REACT_APP_DD_ENV,
    version: import.meta.env.REACT_APP_VERSION,
    sessionSampleRate: 100,
    sessionPersistence: "local-storage",
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error", "warn"],
    beforeSend: (log, context) => {
      if (
        isIgnorableNetworkTransportEvent({
          origin: log.origin,
          isAborted: Boolean(context && "isAborted" in context && context.isAborted),
        })
      ) {
        return false;
      }
      if (isThirdPartyNoiseUrl(log.http?.url)) {
        return false;
      }
      const logMessage = typeof log.message === "string" ? log.message : undefined;
      if (isSegmentPerformanceMetricsNoise(log.error?.message ?? logMessage)) {
        return false;
      }
      return true;
    },
  });

  datadogRum.setGlobalContextProperty("origin", location.origin);
  datadogLogs.setGlobalContextProperty("origin", location.origin);
};
